import { useCallback, useEffect, useState } from "react";
import {
  DISCOUNT_TYPE,
  DISCOUNT_KEY,
} from "../../../pages/promotions/constants";
import { validateDiscountAmounts } from "../utils/discountValidation";

const checkHasDiscount = (discountVO, discountType, offerType) => {
  const keys = DISCOUNT_KEY[offerType];
  if (!keys) return false;

  if (discountType === DISCOUNT_TYPE.DAY_WISE) {
    const map = discountVO?.[keys.map] || {};
    return Object.values(map).some(
      (value) => value && Number.parseFloat(value) > 0
    );
  }
  return discountVO?.[keys.amount] > 0;
};

/**
 * Custom hook for CreatePromo form validation
 * Computes isLaunchDisabled / isDraftDisabled flags from current promo state
 */
export const useCreatePromoValidation = ({
  promo,
  rooms,
  ratePlans,
  isMarket,
}) => {
  const [isLaunchDisabled, setIsLaunchDisabled] = useState(true);
  const [isDraftDisabled, setIsDraftDisabled] = useState(true);

  const checkHasRoomRateplanSelection = useCallback(
    (
      selectedProperties,
      allowedRateRoomCodesByProperty,
      allowedRateRoomCodes,
      roomsRateplansMode
    ) => {
      if (selectedProperties.length > 1 && allowedRateRoomCodesByProperty) {
        // Every selected property must have room/rateplan combos saved
        return selectedProperties.every((propertyId) => {
          const combos = allowedRateRoomCodesByProperty[propertyId];
          return combos && combos.length > 0;
        });
      }

      if (allowedRateRoomCodes && allowedRateRoomCodes.length > 0) {
        return true;
      }

      // Fallback only for "all" mode — "select" mode requires explicit drawer selections
      if (roomsRateplansMode !== "select") {
        return rooms?.length > 0 && ratePlans?.length > 0;
      }

      return false;
    },
    [rooms, ratePlans]
  );

  // Validate form to enable/disable Launch Promo button
  useEffect(() => {
    const promoName = promo?.rateRule?.ruleVO?.descriptionVO?.name;
    const discountVO = promo?.rateRule?.ruleVO?.discountVO;
    const offerType = discountVO?.mCompDiscountType;
    const discountType = discountVO?.discountType;
    const selectedProperties = promo?.selectedProperties || [];

    const hasDiscount = checkHasDiscount(discountVO, discountType, offerType);
    const hasRoomRateplanSelection = checkHasRoomRateplanSelection(
      selectedProperties,
      promo?.allowedRateRoomCodesByProperty || {},
      promo?.allowedRateRoomCodes || [],
      promo?.roomsRateplansMode
    );
    const hasProperties = isMarket || selectedProperties.length > 0;
    // A property change is fetching that property's rooms/rate plans; the
    // selection is momentarily empty, so submitting now would be rejected.
    const isLoadingRoomRatePlans = promo?.roomRatePlansLoading === true;
    // Over-100% amounts are kept in the field and flagged there (see
    // useOfferTypeState.discountLimitError), so the footer is what stops them
    // being launched — same check, so the warning and the disabled button
    // always agree.
    const hasInvalidDiscountAmount = validateDiscountAmounts(promo).length > 0;

    const isValid =
      promoName?.trim() &&
      offerType &&
      discountType &&
      hasDiscount &&
      hasRoomRateplanSelection &&
      hasProperties &&
      !isLoadingRoomRatePlans &&
      !hasInvalidDiscountAmount;
    setIsLaunchDisabled(!isValid);
  }, [promo, rooms, ratePlans, isMarket, checkHasRoomRateplanSelection]);

  useEffect(() => {
    const promoName = promo?.rateRule?.ruleVO?.descriptionVO?.name;
    const discountVO = promo?.rateRule?.ruleVO?.discountVO;
    const offerType = discountVO?.mCompDiscountType;
    const discountType = discountVO?.discountType;
    const hasDiscount = checkHasDiscount(discountVO, discountType, offerType);
    // A draft is held to the cap too: an over-100% discount is invalid whether
    // it is launched now or saved for later.
    setIsDraftDisabled(
      !promoName?.trim() ||
        !hasDiscount ||
        promo?.roomRatePlansLoading === true ||
        validateDiscountAmounts(promo).length > 0
    );
  }, [promo]);

  return { isLaunchDisabled, isDraftDisabled };
};
